import { FC, createContext, useContext, useState } from 'react';

import { Nullable } from '~globals/types/commons';
import { useRequest } from '~hooks/index';
import { getAdminIntegrationStockLocations } from '~services/admin/integration/stockLocation';
// eslint-disable-next-line max-len
import { AdminIntegrationStockLocationExtended } from '~services/admin/integration/stockLocation/types';

import {
  DialogIntegrationAssignWarehousesContextValue,
  DialogIntegrationAssignWarehousesProps,
  DialogIntegrationAssignWarehousesPanelId,
} from './types';

const DialogIntegrationAssignWarehousesContext =
  createContext<DialogIntegrationAssignWarehousesContextValue>({
    open: false,
    onClose: () => {},
    onConfirmSuccess: undefined,
    companyIntegrationId: '',
    activeTab: 'emptyState',
    setActiveTab: () => {},
    stockLocations: [],
    errorStockLocations: null,
    loadingStockLocations: true,
  });

export const useDialogIntegrationAssignWarehousesContext =
  (): DialogIntegrationAssignWarehousesContextValue =>
    useContext(DialogIntegrationAssignWarehousesContext);

const DialogIntegrationAssignWarehousesProvider: FC<
  DialogIntegrationAssignWarehousesProps
> = ({ children, ...restProps }) => {
  const [activeTab, setActiveTab] =
    useState<DialogIntegrationAssignWarehousesPanelId>('emptyState');

  const [stockLocations, setStockLocations] = useState<
    AdminIntegrationStockLocationExtended[]
  >([]);
  const [errorStockLocations, setErrorStockLocations] =
    useState<Nullable<string>>(null);

  const [, loadingStockLocations] = useRequest({
    request: getAdminIntegrationStockLocations,
    payload: restProps.companyIntegrationId,
    withPostSuccess: (response) => {
      const currentStockLocations = response.data
        ?.data as AdminIntegrationStockLocationExtended[];

      if (currentStockLocations.length > 0) {
        setActiveTab('selection');
        setStockLocations(currentStockLocations);
      }
    },
    withPostFailure: (err) => {
      if (err?.data?.data?.message) {
        setErrorStockLocations(err.data.data.message);
      }
    },
  });

  return (
    <DialogIntegrationAssignWarehousesContext.Provider
      value={{
        ...restProps,
        activeTab,
        setActiveTab,
        stockLocations,
        errorStockLocations,
        loadingStockLocations,
      }}
    >
      {children}
    </DialogIntegrationAssignWarehousesContext.Provider>
  );
};

export default DialogIntegrationAssignWarehousesProvider;
